import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { addDays, format, formatDistanceToNow, parseISO, startOfDay } from 'date-fns'
import {
  Building2,
  Calendar,
  CheckSquare,
  FileSignature,
  FileText,
  Hand,
  LifeBuoy,
  Package,
  Target,
  User,
  UserPlus,
  type LucideIcon,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useUser } from '@/contexts/UserContext'
import { useWelcomeSummary } from '@/hooks/use-api'
import { localToday } from '@/hooks/use-welcome-back'
import { explainError } from '@/lib/explain-error'
import { formatCurrency } from '@/lib/utils'
import type { WelcomeRecentType, WelcomeSectionKey, WelcomeSummary } from '@/types'

/**
 * Where you left off, and what needs you next. Opened by StartupDialogs on the first real login
 * of the day; this component only renders it.
 *
 * Sections the person's role cannot see never arrive from the API, and are not rendered at all.
 * Sections that arrive empty say so in one plain line, because "nothing due" is useful to know.
 */

const RECENT_ICONS: Record<WelcomeRecentType, LucideIcon> = {
  Account: Building2,
  Contact: User,
  Lead: UserPlus,
  Opportunity: Target,
  Quote: FileText,
  Order: Package,
  Task: CheckSquare,
  Event: Calendar,
}

const linkClass =
  'rounded text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

function todayWindow() {
  const start = startOfDay(new Date())
  return {
    client_date: localToday(start),
    day_start: start.toISOString(),
    day_end: addDays(start, 1).toISOString(),
  }
}

const shortDate = (ymd: string) => format(parseISO(ymd), 'd MMM')

interface WelcomeBackDialogProps {
  open: boolean
  onClose: () => void
}

export function WelcomeBackDialog({ open, onClose }: WelcomeBackDialogProps) {
  const { currentUser } = useUser()
  const [range] = useState(todayWindow)
  const { data, isLoading, isError, error, refetch, isFetching } = useWelcomeSummary(open, range)

  const firstName = currentUser?.name?.trim().split(/\s+/)[0]

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hand className="h-5 w-5" aria-hidden="true" />
            {firstName ? `Welcome back, ${firstName}` : 'Welcome back'}
          </DialogTitle>
          <DialogDescription>
            {data ? describe(data) : 'What you were last working on, and what needs you next.'}
          </DialogDescription>
        </DialogHeader>

        <div
          className="overflow-y-auto pr-1"
          tabIndex={0}
          role="group"
          aria-label="Your summary"
          aria-busy={isLoading || isFetching}
        >
          {isLoading ? (
            <LoadingState />
          ) : isError ? (
            <div role="alert" className="space-y-2 text-sm">
              <p>{explainError(error, "We couldn't load your summary")}</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Try again
              </Button>
            </div>
          ) : data ? (
            <Summary data={data} onNavigate={onClose} />
          ) : null}
        </div>

        <DialogFooter>
          <p className="mr-auto self-center text-xs text-muted-foreground">
            Shown once a day when you log in.
          </p>
          {/* "Done", not "Close": the corner X is already named Close, and two controls with one
              name are ambiguous to a screen reader. */}
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** One sentence a screen reader hears first, so the counts are known before the lists. */
function describe(data: WelcomeSummary): string {
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
  const parts: string[] = []
  for (const key of data.order) {
    switch (key) {
      case 'tasks':
        if (data.tasks?.total) {
          parts.push(
            `${plural(data.tasks.total, 'task', 'tasks')} due` +
              (data.tasks.overdue > 0 ? ` (${data.tasks.overdue} overdue)` : '')
          )
        }
        break
      case 'events':
        if (data.events?.total)
          parts.push(`${plural(data.events.total, 'meeting', 'meetings')} today`)
        break
      case 'waiting':
        if (data.waiting?.total) parts.push(`${data.waiting.total} waiting on you`)
        break
      case 'closing':
        if (data.closing?.total) {
          parts.push(`${plural(data.closing.total, 'deal', 'deals')} closing or renewing soon`)
        }
        break
      case 'tickets':
        if (data.tickets?.total) {
          parts.push(
            `${plural(data.tickets.total, 'open ticket', 'open tickets')}` +
              (data.tickets.breached > 0 ? ` (${data.tickets.breached} past SLA)` : '')
          )
        }
        break
      case 'orders_queue':
        if (data.orders_queue?.total) {
          parts.push(`${plural(data.orders_queue.total, 'order', 'orders')} to process`)
        }
        break
      case 'draft_orders':
        if (data.draft_orders?.total) {
          parts.push(`${plural(data.draft_orders.total, 'draft order', 'draft orders')}`)
        }
        break
      case 'leads':
        if (data.leads?.total) parts.push(`${plural(data.leads.total, 'new lead', 'new leads')}`)
        break
    }
  }
  if (parts.length === 0) return 'Nothing is due or waiting on you right now.'
  return `You have ${joinList(parts)}.`
}

function joinList(parts: string[]): string {
  if (parts.length <= 1) return parts.join('')
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

function LoadingState() {
  return (
    <div className="grid gap-4 sm:grid-cols-2" aria-hidden="true">
      {[0, 1].map((col) => (
        <div key={col} className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      ))}
      <span className="sr-only">Loading your summary</span>
    </div>
  )
}

/** A record link that also closes the dialog, so the page behind it is what they land on. */
function ItemLink({
  to,
  onNavigate,
  children,
}: {
  to: string | null
  onNavigate: () => void
  children: ReactNode
}) {
  return to ? (
    <Link to={to} onClick={onNavigate} className={linkClass}>
      {children}
    </Link>
  ) : (
    <span className="text-sm font-medium">{children}</span>
  )
}

function Summary({ data, onNavigate }: { data: WelcomeSummary; onNavigate: () => void }) {
  return (
    <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
      <section aria-labelledby="welcome-recent">
        <h3 id="welcome-recent" className="text-sm font-semibold mb-2">
          Where you left off
        </h3>
        {data.recent.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No changes from you in the last two weeks.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {data.recent.map((r) => {
              const Icon = RECENT_ICONS[r.type] ?? FileText
              return (
                <li key={`${r.type}-${r.id}`} className="flex items-start gap-2">
                  <Icon
                    className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <ItemLink onNavigate={onNavigate} to={r.url_path}>
                      {r.name}
                    </ItemLink>
                    <p className="text-xs text-muted-foreground">
                      {r.type}, changed{' '}
                      {formatDistanceToNow(new Date(r.last_changed), { addSuffix: true })}
                    </p>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section aria-labelledby="welcome-todo" className="space-y-4">
        <h3 id="welcome-todo" className="sr-only">
          To do
        </h3>

        {data.order.map((key) => (
          <SectionFor key={key} sectionKey={key} data={data} onNavigate={onNavigate} />
        ))}
      </section>
    </div>
  )
}

/**
 * One to-do section. The server decides which sections this person gets and in what order
 * (welcomeSummaryService ROLE_LAYOUTS); this only knows how to draw each one.
 */
function SectionFor({
  sectionKey,
  data,
  onNavigate,
}: {
  sectionKey: WelcomeSectionKey
  data: WelcomeSummary
  onNavigate: () => void
}) {
  switch (sectionKey) {
    case 'tasks': {
      const tasks = data.tasks
      if (!tasks) return null
      return (
        <Group
          id="welcome-tasks"
          icon={CheckSquare}
          title="Tasks due"
          total={tasks.total}
          shown={tasks.items.length}
          empty="Nothing overdue or due today."
          viewAll={{ to: '/tasks?status=open', label: 'all open tasks' }}
          onNavigate={onNavigate}
        >
          {tasks.items.map((t) => (
            <li key={t.id}>
              <ItemLink onNavigate={onNavigate} to={t.url_path}>
                {t.subject}
              </ItemLink>
              <p className="text-xs text-muted-foreground">
                {t.overdue ? (
                  <span className="font-semibold text-destructive">
                    Overdue, due {shortDate(t.due)}
                  </span>
                ) : (
                  'Due today'
                )}
                {t.priority ? `, ${t.priority} priority` : ''}
                {t.related_name ? `, ${t.related_name}` : ''}
              </p>
            </li>
          ))}
        </Group>
      )
    }
    case 'events': {
      const events = data.events
      if (!events) return null
      return (
        <Group
          id="welcome-events"
          icon={Calendar}
          title="Today's meetings"
          total={events.total}
          shown={events.items.length}
          empty="No meetings today."
          viewAll={{ to: '/events', label: 'all events' }}
          onNavigate={onNavigate}
        >
          {events.items.map((e) => (
            <li key={e.id}>
              <ItemLink onNavigate={onNavigate} to={e.url_path}>
                {e.subject}
              </ItemLink>
              <p className="text-xs text-muted-foreground">
                {e.all_day ? 'All day' : format(new Date(e.start), 'h:mm a')}
                {e.location ? `, ${e.location}` : ''}
              </p>
            </li>
          ))}
        </Group>
      )
    }
    case 'waiting': {
      const waiting = data.waiting
      if (!waiting) return null
      return (
        <Group
          id="welcome-waiting"
          icon={FileSignature}
          title="Waiting on you"
          total={waiting.total}
          shown={waiting.items.length}
          empty="Nobody is waiting on you."
          onNavigate={onNavigate}
        >
          {waiting.items.map((w) => (
            <li key={`${w.kind}-${w.id}`}>
              <ItemLink onNavigate={onNavigate} to={w.url_path}>
                {w.title}
              </ItemLink>
              {w.context && <p className="text-xs text-muted-foreground">{w.context}</p>}
            </li>
          ))}
        </Group>
      )
    }
    case 'closing': {
      const closing = data.closing
      if (!closing) return null
      return (
        <Group
          id="welcome-closing"
          icon={Target}
          title="Closing or renewing soon"
          total={closing.total}
          shown={closing.items.length}
          empty="No deals closing in the next two weeks."
          viewAll={{ to: '/opportunities', label: 'all opportunities' }}
          onNavigate={onNavigate}
        >
          {closing.items.map((c) => (
            <li key={`${c.kind}-${c.id}`}>
              <ItemLink onNavigate={onNavigate} to={c.url_path}>
                {c.name}
              </ItemLink>
              <p className="text-xs text-muted-foreground">
                {c.kind === 'renewal' ? 'Renews' : 'Closes'} {shortDate(c.due)}
                {c.amount_cad != null ? `, ${formatCurrency(c.amount_cad, 'CAD')}` : ''}
                {c.account_name ? `, ${c.account_name}` : ''}
              </p>
            </li>
          ))}
        </Group>
      )
    }
    case 'tickets': {
      const tickets = data.tickets
      if (!tickets) return null
      return (
        <Group
          id="welcome-tickets"
          icon={LifeBuoy}
          title="Your open tickets"
          total={tickets.total}
          shown={tickets.items.length}
          empty="No open tickets assigned to you."
          viewAll={{ to: '/tickets', label: 'all tickets' }}
          onNavigate={onNavigate}
        >
          {tickets.items.map((t) => (
            <li key={t.id}>
              <ItemLink onNavigate={onNavigate} to={t.url_path}>
                #{t.ticket_number} {t.subject}
              </ItemLink>
              <p className="text-xs text-muted-foreground">
                {t.breached && (
                  <span className="font-semibold text-destructive">SLA breached, </span>
                )}
                {t.priority} priority, {t.status}
                {t.account_name ? `, ${t.account_name}` : ''}
              </p>
            </li>
          ))}
        </Group>
      )
    }
    case 'orders_queue':
    case 'draft_orders': {
      const orders = data[sectionKey]
      if (!orders) return null
      const queue = sectionKey === 'orders_queue'
      return (
        <Group
          id={`welcome-${sectionKey}`}
          icon={Package}
          title={queue ? 'Orders to process' : 'Your draft orders'}
          total={orders.total}
          shown={orders.items.length}
          empty={queue ? 'No orders waiting to be processed.' : 'No draft orders.'}
          viewAll={{ to: '/orders', label: 'all orders' }}
          onNavigate={onNavigate}
        >
          {orders.items.map((o) => (
            <li key={o.id}>
              <ItemLink onNavigate={onNavigate} to={o.url_path}>
                {o.name}
              </ItemLink>
              <p className="text-xs text-muted-foreground">
                {queue ? 'Submitted' : 'Started'}{' '}
                {o.created_date
                  ? formatDistanceToNow(new Date(o.created_date), { addSuffix: true })
                  : 'date unknown'}
                {o.amount_cad != null ? `, ${formatCurrency(o.amount_cad, 'CAD')}` : ''}
                {o.account_name ? `, ${o.account_name}` : ''}
              </p>
            </li>
          ))}
        </Group>
      )
    }
    case 'leads': {
      const leads = data.leads
      if (!leads) return null
      return (
        <Group
          id="welcome-leads"
          icon={UserPlus}
          title="New leads assigned to you"
          total={leads.total}
          shown={leads.items.length}
          empty="No new leads waiting for you."
          viewAll={{ to: '/leads', label: 'all leads' }}
          onNavigate={onNavigate}
        >
          {leads.items.map((l) => (
            <li key={l.id}>
              <ItemLink onNavigate={onNavigate} to={l.url_path}>
                {l.name}
              </ItemLink>
              <p className="text-xs text-muted-foreground">
                {l.company ? `${l.company}, ` : ''}
                assigned{' '}
                {l.created_date
                  ? formatDistanceToNow(new Date(l.created_date), { addSuffix: true })
                  : 'recently'}
              </p>
            </li>
          ))}
        </Group>
      )
    }
    default:
      return null
  }
}

interface GroupProps {
  id: string
  icon: LucideIcon
  title: string
  total: number
  shown: number
  empty: string
  viewAll?: { to: string; label: string }
  onNavigate: () => void
  children: ReactNode
}

function Group({
  id,
  icon: Icon,
  title,
  total,
  shown,
  empty,
  viewAll,
  onNavigate,
  children,
}: GroupProps) {
  const more = total - shown
  return (
    <div>
      <h4 id={id} className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold">
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        {title}
        {total > 0 && <span className="font-normal text-muted-foreground">({total})</span>}
      </h4>
      {total === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <>
          <ul aria-labelledby={id} className="space-y-1.5">
            {children}
          </ul>
          {more > 0 &&
            (viewAll ? (
              <Link
                to={viewAll.to}
                onClick={onNavigate}
                className="mt-1 inline-block rounded text-xs text-primary underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {more} more, see {viewAll.label}
              </Link>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">and {more} more</p>
            ))}
        </>
      )}
    </div>
  )
}
